import { driverListQuerySchema, type DriverListQuery, type Page } from '@chukua/common';
import { useEffect, useState } from 'react';
import { ApiError } from '../../lib/api';
import { toListParams } from '../../lib/list-params';
import { useListQuery } from '../../lib/use-list-query';
import { listDrivers, type DriverListItem } from './drivers.api';

/** `queryKey` records which query the loaded page answers, so a newer query shows as refreshing over the old rows. */
export type DriversState =
  { status: 'loading' } | { status: 'loaded'; page: Page<DriverListItem>; queryKey: string } | { status: 'forbidden' } | { status: 'error' };

const keyOf = (query: DriverListQuery) => toListParams(query).toString();

function toState(page: Page<DriverListItem>, query: DriverListQuery): DriversState {
  return { status: 'loaded', page, queryKey: keyOf(query) };
}

function toErrorState(err: unknown): DriversState {
  return { status: err instanceof ApiError && err.status === 403 ? 'forbidden' : 'error' };
}

/** The page of drivers the URL's list query asks for (`useListQuery`), re-fetched whenever that query changes. */
export function useDriverList() {
  const list = useListQuery(driverListQuerySchema);
  const { query } = list;
  const [state, setState] = useState<DriversState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    listDrivers(query)
      .then((page) => {
        if (!cancelled) setState(toState(page, query));
      })
      .catch((err) => {
        if (!cancelled) setState(toErrorState(err));
      });

    return () => {
      cancelled = true;
    };
  }, [query]);

  const isRefreshing = state.status === 'loaded' && state.queryKey !== keyOf(query);

  /** Re-fetches the current page after a mutation commits — a plain async function, not tied to the effect above. */
  async function reload(): Promise<void> {
    try {
      setState(toState(await listDrivers(query), query));
    } catch (err) {
      setState(toErrorState(err));
    }
  }

  return { state, list, isRefreshing, reload };
}
